import './App.css'
import { lazy, Suspense, useCallback, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom'
import NotFound from './pages/not-found/Index.jsx'
import ProteccionDeRutas from './common/components/routes/ProteccionDeRutas.jsx'
import { Header } from '@artprov/design-system'
import { rutasProtegidas, rutasPublicas } from './common/config/routes.config.jsx'
import ClientesHeader from './common/components/layout/ClientesHeader.jsx'
import ClientesMenu from './common/components/layout/ClientesMenu.jsx'
import BannerDdjjPendiente from './common/components/layout/BannerDdjjPendiente.jsx'
import AvisoCargaDdjj from './common/components/ddjj/AvisoCargaDdjj.jsx'
import { getProtectedContentClassName, MOBILE_MENU_OPEN_CLASS_NAME } from './common/utils/layout/protected-content.js'

// La demo del menú es una herramienta interna: se expone solo en desarrollo
// local (`vite dev`) y en el ambiente dev desplegado (`VITE_ENV_URL_PREFIX=dev`),
// nunca en test, beta ni prod. Vite reemplaza ambas variables por literales en el
// build, así que fuera de dev la condición queda en `false` y el import dinámico
// se elimina del bundle.
const MenuDemoPage =
  import.meta.env.DEV || import.meta.env.VITE_ENV_URL_PREFIX === 'dev'
    ? lazy(() => import('./pages/menu-demo/Index.jsx'))
    : null

function LayoutProtegido() {
  const { pathname } = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileInitialFooterItemId, setMobileInitialFooterItemId] = useState(null)
  const mobileTriggerRef = useRef(null)
  const contentClassName = getProtectedContentClassName(pathname, mobileMenuOpen)

  const handleMobileOpenChange = useCallback((isOpen) => {
    setMobileMenuOpen(isOpen)
    if (!isOpen) {
      setMobileInitialFooterItemId(null)
      requestAnimationFrame(() => mobileTriggerRef.current?.focus())
    }
  }, [])

  const openMobileProfile = useCallback(() => {
    setMobileInitialFooterItemId('profile')
    setMobileMenuOpen(true)
  }, [])

  return (
    <>
      <ClientesHeader
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuToggle={() => handleMobileOpenChange(!mobileMenuOpen)}
        showMobileMenuTrigger
        mobileTriggerRef={mobileTriggerRef}
      />
      <ClientesMenu
        mobileMenuOpen={mobileMenuOpen}
        mobileInitialFooterItemId={mobileInitialFooterItemId}
        onMobileOpenChange={handleMobileOpenChange}
      />
      {/* PBI 81906: banner a todo el ancho, fuera del contenedor del contenido (que tiene
          márgenes laterales). Con el menú mobile abierto se oculta igual que el contenido. */}
      <div className={mobileMenuOpen ? MOBILE_MENU_OPEN_CLASS_NAME : undefined}>
        <BannerDdjjPendiente />
      </div>
      {/* PBI 81147 (D6): toast global de carga exitosa de la DDJJ, compartido por el
          banner y la grilla Mis cuentas. El modal se desmonta al cerrarse. */}
      <AvisoCargaDdjj />
      <div className={contentClassName}>
        <Outlet context={{ openMobileProfile }} />
      </div>
    </>
  )
}

function LayoutPublico() {
  return (
    <>
      <Header showDropdown={false} showNotifications={false} logoHref="#" />
      <div className="mt-[20px] lg:mx-[96px] mx-[40px]">
        <Outlet />
      </div>
    </>
  )
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        <Route element={<LayoutPublico />}>
          {rutasPublicas.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        <Route
          element={
            <ProteccionDeRutas rutasConfig={rutasProtegidas}>
              <LayoutProtegido />
            </ProteccionDeRutas>
          }
        >
          {rutasProtegidas.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Route>

        {MenuDemoPage ? (
          <Route
            path="/menu-demo"
            element={
              <Suspense fallback={null}>
                <MenuDemoPage />
              </Suspense>
            }
          />
        ) : null}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
